import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { callWithAuth, getUserSettings, updateUserSettings } from "../api/Api";
import { UserSettings, PutUserSettingsRequest } from "@shared_types";

const USER_SETTINGS_QUERY_KEY = "userSettings";

export interface UseUserSettingsReturn {
  settings: UserSettings | undefined;
  isLoading: boolean;
  error: unknown;
  updateSettings: (settings: Partial<UserSettings>) => Promise<void>;
}

/**
 * Hook for managing user settings
 * @returns {UseUserSettingsReturn} The user settings and functions to update them
 */
export const useUserSettings = (): UseUserSettingsReturn => {
  const queryClient = useQueryClient();

  // Fetch user settings
  const { data, isLoading, error } = useQuery({
    queryKey: [USER_SETTINGS_QUERY_KEY],
    queryFn: () => callWithAuth(getUserSettings),
    staleTime: 60000, // Keep cache fresh for 1 minute
  });

  // Mutation for updating user settings
  const mutation = useMutation({
    mutationFn: (newSettings: Partial<UserSettings>) => {
      const currentSettings = data?.settings;
      if (!currentSettings) {
        throw new Error("Cannot update settings before they are loaded");
      }

      const updatedSettings: UserSettings = {
        ...currentSettings,
        ...newSettings,
      };

      const request: PutUserSettingsRequest = {
        settings: updatedSettings,
      };

      return callWithAuth(updateUserSettings, request);
    },
    // Optimistically update the cache for immediate UI feedback
    onMutate: async (newPartialSettings) => {
      // Cancel any outgoing refetches to avoid overwriting our optimistic update
      await queryClient.cancelQueries({ queryKey: [USER_SETTINGS_QUERY_KEY] });

      // Snapshot the previous value
      const previousSettings = queryClient.getQueryData([
        USER_SETTINGS_QUERY_KEY,
      ]);

      // Optimistically update the settings in the cache
      if (data?.settings) {
        queryClient.setQueryData([USER_SETTINGS_QUERY_KEY], {
          settings: {
            ...data.settings,
            ...newPartialSettings,
          },
        });
      }

      // Return the snapshot so we can rollback on error
      return { previousSettings };
    },
    // If the mutation fails, roll back to the previous value
    onError: (err, _newSettings, context) => {
      if (context?.previousSettings) {
        queryClient.setQueryData(
          [USER_SETTINGS_QUERY_KEY],
          context.previousSettings
        );
      }
      console.error("Error updating user settings:", err);
    },
    // After success or failure, invalidate the query to ensure fresh data
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [USER_SETTINGS_QUERY_KEY] });
    },
  });

  // Function to update settings
  const updateSettings = async (newSettings: Partial<UserSettings>) => {
    await mutation.mutateAsync(newSettings);
  };

  // Get settings or default values
  const settings = data?.settings;

  return {
    settings,
    isLoading,
    error,
    updateSettings,
  };
};
